<template>
  <v-container fluid class="custom-container">
    <!-- Folders Header -->
    <v-layout
      v-if="!isEmpty(groupedFolders)"
      class="mb-2 list-folders-actions align-center"
      row
      wrap
    >
      <v-flex xs12 sm6 md3>
        <div class="d-flex align-center">
          <span class="mr-2">Exibindo {{ pageStart }} - {{ pageStop }} de {{ foldersCount }}</span>
          <v-btn icon small @click="pageNumber--" :disabled="pageNumber <= 1">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <v-btn icon small @click="pageNumber++" :disabled="pageNumber >= pages">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </v-flex>
      <v-flex xs12 sm6 md3>
        <v-select
          v-model="orderFilter"
          :items="orderFilters"
          item-text="name"
          return-object
          hide-details
          solo
          flat
          dense
        />
      </v-flex>
      <v-flex xs12 sm6 md3 class="justify-center" style="display: flex">
        <v-btn-toggle v-model="viewMode" mandatory dense>
          <v-btn flat small :value="'list'">
            <v-icon>mdi-format-list-bulleted</v-icon>
          </v-btn>
          <!-- <v-btn flat small :value="'grid'">
            <v-icon>mdi-grid</v-icon>
          </v-btn>-->
        </v-btn-toggle>
      </v-flex>
    </v-layout>

    <!-- Vista em Lista -->
    <template v-if="viewMode === 'list'">
      <section class="folders">
        <v-layout
          class="folder-list mt-4"
          v-for="(group,index) in groupedFolders"
          :key="index"
          column
        >
          <span class="subheading grey--text my-2 text-capitalize">{{ index }}</span>
          <div class="elevation-1 group" v-if="group.length > 0">
            <transition-group
              name="fade-cascade"
              tag="div"
              class="cascade-group"
              appear
            >
              <folder-card
                v-for="(folder, folderIndex) in group"
                :key="folder.id"
                :folder="folder"
                :style="{ '--cascade-delay': `${folderIndex * 0.05}s` }"
                @open-folder="openFolder"
              />
            </transition-group>
          </div>
        </v-layout>
      </section>
    </template>

    <!-- Vista em Grid -->
    <template v-else>
      <section class="folders">
        <v-layout
          class="folder-list mt-4"
          v-for="(group,index) in groupedFolders"
          :key="index"
          column
        >
          <span class="subheading grey--text my-2 text-capitalize">{{ index }}</span>
          <folder-grid
            :folders="group"
            v-if="group.length > 0"
          />
        </v-layout>
      </section>
    </template>

    <!-- Empty Content -->
    <v-container fill-height v-if="isEmpty(groupedFolders) && !loading.show">
      <v-layout column align-center justify-center>
        <v-icon class="mb-2" size="48" color="grey darken-2">mdi-inbox</v-icon>
        <span class="title font-weight-regular grey--text text--darken-2">
          Não foram encontrados resultados para sua busca
        </span>
      </v-layout>
    </v-container>
  </v-container>
</template>

<script>
import { isEmpty, chain } from 'lodash';
import * as ptLocale from 'date-fns/locale/pt';
import format from 'date-fns/format';
import CurrentOwner from '@/components/inbox/CurrentOwner';
import TermComponent from '@/components/inbox/TermComponent';

// Lazy loading de componentes
const FolderCard = () => ({
  component: import('@/components/folder/FolderCard'),
  loading: {
    template: '<div class="folder-card-skeleton"></div>'
  },
  delay: 200,
  timeout: 3000
});

const FolderGrid = () => ({
  component: import('@/components/folder/FolderGrid'),
  loading: {
    template: '<div class="folder-grid-skeleton"></div>'
  },
  delay: 200,
  timeout: 3000
});

export default {
  name: 'list-all-folders',
  components: {
    CurrentOwner,
    TermComponent,
    FolderCard,
    FolderGrid
  },
  mounted() {
    window.addEventListener('admin-unit-changed', this.handleAdminUnitChange);
  },
  beforeDestroy() {
    window.removeEventListener('admin-unit-changed', this.handleAdminUnitChange);
  },
  data() {
    return {
      pageNumber: 1,
      rowsPerPage: 20,
      isEmpty,
      viewMode: 'list',
      orderFilters: [
        {
          name: "Tramitação mais antiga",
          property: "effective_updated_at",
          order: "asc"
        },
        {
          name: "Tramitação mais recente",
          property: "effective_updated_at",
          order: "desc"
        }
      ],
      orderFilter: {
        name: "Tramitação mais recente",
        property: "effective_updated_at",
        order: "desc"
      }
    };
  },
  methods: {
    openFolder(id) {
      const route = this.$router.resolve(`/pastas/${id}/show`);
      window.open(route.href, '_blank');
    },
    getFolders() {
      if (this.searchText && this.searchText.length > 2) {
        this.$store.dispatch('setLoadingSearchText', true);
        this.$store.dispatch('getFoldersSearch', {
          page: this.pageNumber,
          limit: this.rowsPerPage,
          searchText: this.searchText || '',
          order: this.orderFilter.order,
          sort: this.orderFilter.property
        }).finally(() => {
          this.$store.dispatch('setLoadingSearchText', false);
        });
        this.$store.dispatch('getFoldersCount');
      } else {
        this.$store.dispatch('setLoadingSearchText', false);
      }
    },
    formatDate(date) {
      return format(date, 'DD/MM/YYYY');
    },
    formatMonthYear(date) {
      return format(date, 'MMMM [de] YYYY', { locale: ptLocale });
    },
    handleAdminUnitChange(event) {
      this.pageNumber = 1;
      this.$nextTick(() => {
        if (this.searchText && this.searchText.length > 2) {
          this.getFolders();
          setTimeout(() => {
            this.$store.dispatch('getFoldersCount', { forceUpdate: true });
          }, 500);
        }
      });
    }
  },
  computed: {
    folders() {
      if (this.$store.getters.folders.length === undefined) {
        return [];
      }
      return this.$store.getters.folders.filter(
        item => item.folder_section === 'search'
      );
    },
    groupedFolders() {
      const group = {};
      const orderedKeys = [];

      this.folders.forEach(folder => {
        try {
          const date = folder.last_transaction
            ? new Date(folder.last_transaction.updated_at)
            : (folder.updated_at ? new Date(folder.updated_at) : null);

          const key = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : 'Sem data';

          if (!group[key]) {
            group[key] = { folders: [folder], date: date };
            orderedKeys.push(key);
          } else {
            group[key].folders.push(folder);
          }
        } catch (error) {
          console.error('[DEBUG] Erro ao processar pasta:', error, folder);
        }
      });

      orderedKeys.sort((a, b) => {
        try {
          if (group[a].date && group[b].date) {
            const comparison = this.orderFilter.order === 'asc'
              ? group[a].date - group[b].date
              : group[b].date - group[a].date;
            return comparison;
          }
          return 0;
        } catch (error) {
          console.error('[DEBUG] Erro ao comparar datas:', error, { a, b });
          return 0;
        }
      });

      const orderedGroups = {};
      orderedKeys.forEach(key => {
        try {
          if (!group[key]) {
            console.warn('[DEBUG] Grupo não encontrado:', key);
          }
          if (!group[key].date) {
            console.error('[DEBUG] Grupo sem campo date:', key, group[key]);
          }
          const label = group[key].date
            ? this.formatMonthYear(group[key].date)
            : 'Sem data';
          orderedGroups[label] = group[key].folders || [];
        } catch (error) {
          console.error('[DEBUG] Erro ao processar grupo:', key, error);
        }
      });

      return orderedGroups;
    },
    foldersCount() {
      return this.$store.getters.activeTotalFolders;
    },
    pages() {
      return this.$store.getters.foldersPagesCount;
    },
    pageStart() {
      return this.pageNumber * this.rowsPerPage - (this.rowsPerPage - 1);
    },
    pageStop() {
      return Math.min(
        this.pageStart + (this.rowsPerPage - 1),
        this.foldersCount
      );
    },
    searchText() {
      return this.$store.getters.searchText;
    },
    loading() {
      return this.$store.getters.loading;
    }
  },
  watch: {
    pageNumber: {
      handler: 'getFolders',
      immediate: true
    },
    searchText: {
      handler: 'getFolders'
    },
    orderFilter: {
      handler: 'getFolders'
    }
  }
};
</script>

<style scoped lang="scss">
.custom-container {
  height: calc(100vh - 48px);
  position: relative;
  overflow: hidden;
}

.folders {
  overflow-y: auto;
  height: 100%;
  margin-top: 70px;
}

.list-folders-actions {
  position: absolute;
  z-index: 4;
  width: 97%;
  background: white;
  margin-top: 6px;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 600px) {
    position: relative;
    width: 100%;
    margin-top: 0;
    padding: 4px;
  }
}

// Skeleton loading styles
.folder-card-skeleton {
  height: 72px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.folder-grid-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;

  .folder-card-skeleton {
    height: 200px;
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.folder-list {
  .group {
    position: relative;
    overflow: hidden;
  }
}

.fade-cascade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  transform: translateY(0);
}
.fade-cascade-enter {
  opacity: 0;
  transform: translateY(20px);
}
.fade-cascade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.cascade-group > * {
  transition-delay: var(--cascade-delay, 0s);
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/base/ListAllFolders.vue